/**
 * ProShip Direct v2 — Couriers.
 *
 * Shaped on the panel's own courier list: parent courier, service type and courier code, no
 * logos. ProShip and Direct couriers sit in one table split by tabs, because the decision this
 * screen carries is the same for both — may allocation use it.
 *
 * This replaces the three operating modes. "Own only" is every ProShip courier off; "ProShip
 * only" is every Direct courier off; both on needs no name.
 *
 * Coverage cards are a UNION, not a sum: the question is how many pincodes at least one enabled
 * courier can serve. See data/serviceabilityStats.js — the figures themselves are illustrative.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Grid, Typography, Switch, Button, Tabs, Tab, TextField, InputAdornment, MenuItem,
  Tooltip, Alert, FormControlLabel,
} from '@mui/material';
import { Link } from 'react-router-dom';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import Loader from '../../../common/Loader';
import { cellStackSx, primaryTextSx, secondaryTextSx } from '../../orders/list2/cellStyles';
import {
  pageContainerSx, sectionCardSx, primaryBtnSx, secondaryBtnSx, dataGridWrapSx,
} from '../../../styles/patterns';
import { SUBTLE, NAVY, WARNING, TEAL, BORDER, WHITE, SUCCESS } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import CourierLogo from '../components/CourierLogo';
import { MONO } from '../components/directCellStyles';
import { fmtCount } from '../components/directFormat';
import { statCardSx, statLabelSx, statValueSx, statSubSx } from '../components/stats';

import { useCourierAvailability, OWNER, ACTIVITY_ENTITY } from '../data/hooks';
import { unionCoverage } from '../data/serviceabilityStats';
import { dpPath } from '../flags';
import { OwnerChip, CredentialChip } from '../components/chips';
import { CatalogueError } from '../components/states';
import CourierDetailsModal from '../components/CourierDetailsModal';
import ActivityLogModal from '../components/ActivityLogModal';

const TABS = [
  { key: 'ALL', label: 'All' },
  { key: OWNER.PROSHIP, label: 'ProShip' },
  { key: OWNER.DIRECT, label: 'Direct' },
];

const SHIPMENT_TYPES = ['B2C', 'B2B'];

const fieldSx = {
  '& .MuiInputBase-root': { fontFamily: FONT, fontSize: SIZE['2xs'] },
  '& .MuiInputLabel-root': { fontFamily: FONT, fontSize: SIZE['3xs'] },
};

/**
 * Grams as the merchant reads a slab: 500 becomes 0.5 Kg, 250 becomes 0.25 Kg.
 *
 * Kilograms because that is the unit on every carrier rate sheet and on the courier form
 * beside it. Trailing zeros are trimmed so 1000g is "1 Kg" rather than "1.00 Kg" — a column
 * of decimals nobody needs is a column that is harder to scan.
 *
 * Zero is a real answer and must not read as missing, so only null/undefined and values that
 * are not numbers fall through to the dash.
 */
const slabLabel = (grams) => {
  if (grams === null || grams === undefined || grams === '') return null;
  const n = Number(grams);
  if (!Number.isFinite(n)) return null;
  return `${parseFloat((n / 1000).toFixed(3))} Kg`;
};

/** Two numbers with their own tiny labels — reads faster than a sentence per row. */
function CoverageCell({ coverage }) {
  return (
    <Box sx={{ ...cellStackSx, gap: '1px' }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
        <Typography sx={{ ...statLabelSx, fontSize: SIZE['4xs'], width: 22 }}>LM</Typography>
        <Typography sx={{ ...primaryTextSx, fontVariantNumeric: 'tabular-nums' }}>
          {fmtCount(coverage.lastMile)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
        <Typography sx={{ ...statLabelSx, fontSize: SIZE['4xs'], width: 22 }}>FM</Typography>
        <Typography sx={{ ...secondaryTextSx, fontVariantNumeric: 'tabular-nums' }}>
          {fmtCount(coverage.firstMile)}
        </Typography>
      </Box>
    </Box>
  );
}

function CourierAvailability() {
  const { rows, toggleRow, setRowsEnabled, isLoading, isError, refetch } = useCourierAvailability();
  const [detailsFor, setDetailsFor] = useState(null);
  const [logOpen, setLogOpen] = useState(false);

  const [tab, setTab] = useState('ALL');
  const [query, setQuery] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [slab, setSlab] = useState('');
  const [shipmentType, setShipmentType] = useState('B2C');
  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(20);

  const serviceTypes = useMemo(
    () => [...new Set(rows.map((r) => r.serviceType).filter(Boolean))].sort(),
    [rows]
  );

  /*
    THE SLABS THAT ACTUALLY EXIST, ascending, rather than a fixed list of round numbers.

    Couriers do not agree on slabs — this account alone spans 1g to 500g — so a hardcoded
    0.5/1/2/5 would offer a merchant filters that match nothing and hide the values that do.
    Reading them off the rows means every option returns at least one courier.

    Numeric sort, not lexical: as strings "1000" sorts before "50" and the dropdown reads as
    though it were shuffled.
  */
  const slabs = useMemo(
    () =>
      [...new Set(rows.map((r) => r.minChargeableWeight).filter((w) => slabLabel(w) !== null))].sort(
        (a, b) => Number(a) - Number(b)
      ),
    [rows]
  );

  const directCount = rows.filter((r) => r.owner === OWNER.DIRECT).length;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab !== 'ALL' && r.owner !== tab) return false;
      if (serviceType && r.serviceType !== serviceType) return false;
      /*
      NONE is its own filter, and it is the one worth having: it answers "which couriers am I
      missing this for?", which is a data-quality question a merchant can act on. Kept distinct
      from All rather than folded into it, since "no minimum recorded" is not a weight.
      */
      if (slab === 'NONE' && slabLabel(r.minChargeableWeight) !== null) return false;
      if (slab && slab !== 'NONE' && Number(r.minChargeableWeight) !== Number(slab)) return false;
      if (shipmentType && r.shipmentType && !r.shipmentType.includes(shipmentType)) return false;
      if (!q) return true;
      return (
        r.parent.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.code.toLowerCase().includes(q)
      );
    });
  }, [rows, tab, serviceType, shipmentType, slab, query]);

  const enabled = rows.filter((r) => r.enabled);
  const union = unionCoverage(enabled);

  // The master switch reflects and acts on exactly what is filtered — never the whole catalogue.
  const switchable = visible.filter((r) => !r.blockedFor);
  const allVisibleOn = switchable.length > 0 && switchable.every((r) => r.enabled);

  const cards = [
    {
      label: 'Couriers enabled',
      value: `${enabled.length} / ${rows.length}`,
      sub: `${rows.filter((r) => r.owner === OWNER.DIRECT && r.enabled).length} Direct · ${rows.filter((r) => r.owner === OWNER.PROSHIP && r.enabled).length} ProShip`,
    },
    { label: 'First mile', value: fmtCount(union.firstMile), sub: 'pickup pincodes covered' },
    { label: 'Last mile', value: fmtCount(union.lastMile), sub: 'delivery pincodes covered' },
    { label: 'RVP', value: fmtCount(union.rvp), sub: 'reverse pickup pincodes' },
  ];

  const columns = [
    {
      field: 'parent',
      headerName: 'Parent Courier',
      minWidth: 218,
      flex: 1,
      sortable: false,
      /*
        THE MARK LEADS THE ROW, because this is the one screen where a merchant is scanning
        sixty-odd partners for one they already know by sight — which a logo answers faster
        than reading a name. It does not replace the name: the same carrier appears on
        several rows, one per service line, so the mark groups them and the text still has to
        say which is which.
      */
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, minWidth: 0 }}>
          <CourierLogo name={row.parent} src={row.logo} />
          <Box sx={{ ...cellStackSx, minWidth: 0 }}>
            <Typography sx={primaryTextSx} noWrap>{row.parent}</Typography>
            <Typography sx={{ ...secondaryTextSx, fontFamily: MONO }} noWrap>{row.code}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'serviceType',
      headerName: 'Service Type',
      minWidth: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <Typography sx={secondaryTextSx}>{row.serviceType || '—'}</Typography>
        </Box>
      ),
    },
    {
      field: 'minChargeableWeight',
      headerName: 'Min. Weight Slab',
      minWidth: 128,
      sortable: false,
      /*
        Beside the service type because the two are read together: a surface line and an air
        line on the same carrier bill from different minimums, and that pairing is the whole
        reason the number is worth a column.

        A dash is not a zero. Where the courier record carries no minimum the cell says so and
        the tooltip says where the number comes from, rather than printing a plausible 0.5 Kg
        that the merchant would take for a fact.
      */
      renderCell: ({ row }) => {
        const label = slabLabel(row.minChargeableWeight);
        return (
          <Box sx={cellStackSx}>
            {label ? (
              <Typography sx={primaryTextSx}>{label}</Typography>
            ) : (
              <Tooltip
                title={
                  row.owner === OWNER.DIRECT
                    ? 'Not set on this account. Add a minimum chargeable weight under Weights on the courier form.'
                    : 'This courier record carries no minimum chargeable weight.'
                }
              >
                <Typography sx={secondaryTextSx}>—</Typography>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'owner',
      headerName: 'Type',
      minWidth: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <OwnerChip owner={row.owner} />
        </Box>
      ),
    },
    {
      field: 'coverage',
      headerName: 'Serviceable Pincodes',
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => <CoverageCell coverage={row.coverage} />,
    },
    {
      field: 'blocked',
      headerName: 'Blocked',
      minWidth: 90,
      sortable: false,
      renderCell: ({ row }) => {
        const total = Object.values(row.coverage.blocked).reduce((a, b) => a + b, 0);
        return (
          <Box sx={cellStackSx}>
            <Typography sx={total ? { ...primaryTextSx, color: WARNING } : secondaryTextSx}>
              {total ? fmtCount(total) : '—'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'enabled',
      headerName: 'Status',
      minWidth: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title={row.blockedFor ? 'Credentials must pass first' : ''}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Switch size='small' checked={row.enabled} disabled={row.blockedFor} onChange={() => toggleRow(row)} />
            {row.blockedFor ? (
              <CredentialChip state={row.credentials} />
            ) : (
              <Typography sx={{ ...secondaryTextSx, color: row.enabled ? SUCCESS : SUBTLE }}>
                {row.enabled ? 'Active' : 'Off'}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      minWidth: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Button size='small' sx={secondaryBtnSx} onClick={() => setDetailsFor(row)}>
          Details
        </Button>
      ),
    },
  ];

  /*
    NO PLAN GATE ON THIS PAGE. It used to return the plan-inactive card, which hid the ProShip
    courier list from every merchant without ProShip Direct — and switching ProShip couriers on
    and off has nothing to do with Direct. Worse, it was unrecoverable: a merchant who had turned
    couriers off could not reach the screen to turn them back on.

    Direct accounts are already withheld upstream (see useCourierAvailability), so with no plan
    this page simply lists ProShip couriers and nothing else.
  */
  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Which couriers allocation may use'>Couriers</PageTitle>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.md }}>
        <Grid container spacing={1.25}>
          {cards.map((c) => (
            <Grid item xs={6} md={3} key={c.label}>
              <Box sx={statCardSx}>
                <Typography sx={statLabelSx}>{c.label}</Typography>
                <Typography sx={statValueSx}>{isLoading ? '—' : c.value}</Typography>
                <Typography sx={statSubSx}>{c.sub}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
          <Tabs
            value={tab}
            onChange={(_e, v) => { setTab(v); setPage(0); }}
            sx={{
              minHeight: 34,
              '& .MuiTab-root': {
                fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                textTransform: 'none', minHeight: 34, py: 0, px: 2,
              },
            }}
          >
            {TABS.map((t) => (
              <Tab
                key={t.key}
                value={t.key}
                label={`${t.label}${t.key === OWNER.DIRECT ? ` (${directCount})` : t.key === OWNER.PROSHIP ? ` (${rows.length - directCount})` : ''}`}
              />
            ))}
          </Tabs>
        </Box>

        {tab === OWNER.DIRECT && directCount === 0 ? (
          <Box sx={{ ...sectionCardSx, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: GAP.sm }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY }}>
              No Direct couriers yet
            </Typography>
            <Typography sx={statSubSx}>
              Bring your own courier contract and it appears here alongside ProShip&rsquo;s.
            </Typography>
            <Box sx={{ display: 'flex', gap: GAP.sm, mt: 0.5 }}>
              <Button component={Link} to={dpPath('/home')} variant='contained' size='small' startIcon={<RocketLaunchOutlinedIcon />} sx={primaryBtnSx}>
                ProShip Direct home
              </Button>
              <Button component={Link} to={dpPath('/couriers/connect')} size='small' startIcon={<AddOutlinedIcon />} sx={secondaryBtnSx}>
                Add courier
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {/*
              One row that never wraps: at narrow widths it scrolls sideways instead of dropping
              the primary action onto a second line, which is what "Add Direct courier" was doing.
              The master switch sits with the filters it obeys.
            */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: GAP.sm,
                p: 1,
                bgcolor: WHITE,
                border: `1px solid ${BORDER}`,
                borderRadius: RADIUS.md,
                overflowX: 'auto',
              }}
            >
              <TextField
                size='small'
                placeholder='Search courier or code'
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(0); }}
                sx={{ ...fieldSx, width: 190, flexShrink: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchOutlinedIcon sx={{ fontSize: 15, color: SUBTLE }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select size='small' label='Shipment' value={shipmentType}
                onChange={(e) => { setShipmentType(e.target.value); setPage(0); }}
                sx={{ ...fieldSx, width: 94, flexShrink: 0 }}
              >
                {SHIPMENT_TYPES.map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>{s}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size='small' label='Service' value={serviceType}
                onChange={(e) => { setServiceType(e.target.value); setPage(0); }}
                sx={{ ...fieldSx, width: 116, flexShrink: 0 }}
              >
                <MenuItem value='' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>All</MenuItem>
                {serviceTypes.map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>{s}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size='small' label='Min. slab' value={slab}
                onChange={(e) => { setSlab(e.target.value); setPage(0); }}
                sx={{ ...fieldSx, width: 116, flexShrink: 0 }}
              >
                <MenuItem value='' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>All</MenuItem>
                {slabs.map((w) => (
                  <MenuItem key={w} value={String(w)} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                    {slabLabel(w)}
                  </MenuItem>
                ))}
                <MenuItem value='NONE' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                  Not set
                </MenuItem>
              </TextField>
              {(query || serviceType || slab) && (
                <Button
                  size='small'
                  sx={secondaryBtnSx}
                  onClick={() => { setQuery(''); setServiceType(''); setSlab(''); setPage(0); }}
                >
                  Clear
                </Button>
              )}

              <Box sx={{ flex: 1, minWidth: 8 }} />

              {/*
                NOWRAP AND NO SHRINK. The toolbar is a flex row, so adding the slab filter took
                width from whatever would yield — and this label yielded, breaking "Disable all
                (116)" across two lines and making the row taller than everything beside it.
                Flex items default to shrinking below their content; these two say not to, so the
                toolbar scrolls horizontally (it already has overflowX auto) instead of wrapping
                one control.
              */}
              <FormControlLabel
                sx={{ mr: 0.5, flexShrink: 0 }}
                control={
                  <Switch
                    size='small'
                    checked={allVisibleOn}
                    disabled={switchable.length === 0}
                    onChange={(e) => setRowsEnabled(switchable, e.target.checked)}
                  />
                }
                label={
                  <Typography
                    sx={{ fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, whiteSpace: 'nowrap' }}
                  >
                    {allVisibleOn ? 'Disable' : 'Enable'} all ({switchable.length})
                  </Typography>
                }
              />
              <Button
                size='small'
                startIcon={<HistoryOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{ ...secondaryBtnSx, flexShrink: 0, whiteSpace: 'nowrap' }}
                onClick={() => setLogOpen(true)}
              >
                Activity
              </Button>
              <Button
                component={Link}
                to={dpPath('/couriers/connect')}
                size='small'
                variant='contained'
                startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{ ...primaryBtnSx, flexShrink: 0, whiteSpace: 'nowrap', bgcolor: TEAL, '&:hover': { bgcolor: TEAL, filter: 'brightness(0.94)' } }}
              >
                Add Direct courier
              </Button>
            </Box>

            {enabled.length === 0 && (
              <Alert severity='warning' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], py: 0 }}>
                Nothing is enabled, so no order can be allocated.
              </Alert>
            )}

            {isError ? (
              <CatalogueError onRetry={refetch} />
            ) : isLoading ? (
              <Loader />
            ) : (
              <Box sx={dataGridWrapSx}>
                <ProzoDataGrid
                  columns={columns}
                  rows={visible.slice(page * sizePerPage, page * sizePerPage + sizePerPage)}
                  rowCount={visible.length}
                  page={page}
                  setPage={setPage}
                  sizePerPage={sizePerPage}
                  setSizePerPage={setSizePerPage}
                  paginationMode='client'
                  getRowId={(row) => row.id}
                  disableColumnMenu
                  disableColumnFilter
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Detail lives in a modal so filters, tab and page survive a look at one courier. */}
      {logOpen && (
        <ActivityLogModal
          entity={ACTIVITY_ENTITY.COURIER}
          title='Courier activity'
          onClose={() => setLogOpen(false)}
        />
      )}

      {detailsFor && (
        <CourierDetailsModal
          courier={rows.find((r) => r.id === detailsFor.id) || detailsFor}
          onClose={() => setDetailsFor(null)}
        />
      )}
    </Box>
  );
}

export default withLayout(CourierAvailability);
